import { memo } from 'react';
import { Loader2 } from 'lucide-react';
import { usePropertySceneEditor } from '@/hooks/usePropertySceneEditor';
import { usePropertySceneEditorData } from '@/hooks/usePropertySceneEditorData';
import { usePropertySceneEditorHandlers } from '@/hooks/usePropertySceneEditorHandlers';
import { SettingsBar } from './PropertySceneEditorSettings';
import { PropertySceneEditorHeader } from './PropertySceneEditorHeader';
import { PropertyBulkActionsMemo } from './PropertyBulkActions';
import { PropertySceneListMemo } from './PropertySceneList';
import { PropertySceneEditorModals } from './PropertySceneEditorModals';
import type { IPropertySceneEditorProps } from '@/@types/propertySceneEditor';

function PropertySceneEditor({
  videoId,
  propertyId,
  selectedVoiceId,
  selectedDuration,
  selectedAspectRatio,
  onVoiceChange,
  onDurationChange,
  onAspectRatioChange,
  onGenerateFullVideo,
  isGenerating,
  hasExistingVideo,
  onViewExistingVideo,
}: IPropertySceneEditorProps) {
  const {
    processedImages,
    muxProgress,
    handleMuxAllScenes,
    propertyVideoAdapter,
    isFullVideoReady,
    fullVideoReadyMessage,
    finalVideoUrl,
  } = usePropertySceneEditorData(videoId, propertyId, selectedAspectRatio);

  const {
    scenes,
    isLoading,
    error,
    layout,
    setLayout,
    imageSelectionModalOpen,
    imageSelectionScene,
    promptModalOpen,
    promptModalScene,
    openImageSelectionModal,
    closeImageSelectionModal,
    openPromptModal,
    closePromptModal,
    planScenes,
    isPlanningScenes,
    hasTriggeredPlanning,
    updateVoiceoverText,
    updateImagePrompt,
    updateSelectedImage,
    updateSceneTemplate,
    updateSceneNegativePrompt,
    regenerateVoiceover,
    regenerateVideo,
    deleteScene,
    generateAllVoiceovers,
    regenerateAllVoiceovers,
    generateAllVideos,
    reorderScenes,
    voiceoverProgress,
    videoProgress,
    savingSceneIds,
    regeneratingVoiceoverIds,
    regeneratingVideoIds,
    deletingSceneIds,
    allVoiceoversReady,
    allVideosReady,
  } = usePropertySceneEditor(videoId, selectedVoiceId);

  const {
    isRegeneratingPrompt,
    lightboxOpen,
    lightboxSceneIndex,
    handleImageSelect,
    handlePromptSave,
    handlePromptSaveAndGenerate,
    handleRegeneratePrompt,
    handleExpandVideo,
    handleCloseLightbox,
    handlePlanScenes,
  } = usePropertySceneEditorHandlers(
    videoId,
    propertyId,
    selectedDuration,
    selectedAspectRatio,
    scenes,
    isPlanningScenes,
    hasTriggeredPlanning,
    planScenes,
    imageSelectionScene,
    promptModalScene,
    updateSelectedImage,
    updateImagePrompt,
    updateSceneNegativePrompt,
    regenerateVideo,
    closePromptModal
  );

  if (isLoading || isPlanningScenes) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground mt-2">
          {isPlanningScenes ? 'Planning your video scenes...' : 'Loading scenes...'}
        </span>
        {isPlanningScenes && (
          <p className="text-sm text-muted-foreground mt-2">
            Analyzing images and creating your personalized video script
          </p>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-destructive font-medium">Error loading scenes</p>
        <p className="text-sm text-muted-foreground mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SettingsBar
        selectedVoiceId={selectedVoiceId}
        selectedDuration={selectedDuration}
        selectedAspectRatio={selectedAspectRatio}
        onVoiceChange={onVoiceChange}
        onDurationChange={onDurationChange}
        onAspectRatioChange={onAspectRatioChange}
      />

      {processedImages.length === 0 && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>Warning:</strong> No processed images available for {selectedAspectRatio} aspect
            ratio. Please go back to Step 4 and process images again.
          </p>
        </div>
      )}

      <PropertySceneEditorHeader
        layout={layout}
        onLayoutChange={setLayout}
        hasExistingVideo={hasExistingVideo}
        onViewExistingVideo={onViewExistingVideo}
        onGenerateFullVideo={onGenerateFullVideo}
        isGenerating={isGenerating}
        isFullVideoReady={isFullVideoReady}
        fullVideoReadyMessage={fullVideoReadyMessage}
        finalVideoUrl={finalVideoUrl}
        propertyVideoAdapter={propertyVideoAdapter}
      />

      <PropertyBulkActionsMemo
        hasScenes={scenes.length > 0}
        hasExistingVoiceovers={allVoiceoversReady}
        voiceoverProgress={voiceoverProgress}
        videoProgress={videoProgress}
        muxProgress={muxProgress}
        allVoiceoversReady={allVoiceoversReady}
        allVideosReady={allVideosReady}
        onPlanScenes={handlePlanScenes}
        onGenerateAllVoiceovers={generateAllVoiceovers}
        onRegenerateAllVoiceovers={regenerateAllVoiceovers}
        onGenerateAllVideos={generateAllVideos}
        onMuxAllScenes={handleMuxAllScenes}
        isPlanningScenes={isPlanningScenes}
      />

      <PropertySceneListMemo
        scenes={scenes}
        layout={layout}
        processedImages={processedImages}
        aspectRatio={selectedAspectRatio}
        savingSceneIds={savingSceneIds}
        regeneratingVoiceoverIds={regeneratingVoiceoverIds}
        regeneratingVideoIds={regeneratingVideoIds}
        deletingSceneIds={deletingSceneIds}
        onReorder={reorderScenes}
        onVoiceoverTextChange={updateVoiceoverText}
        onImagePromptChange={updateImagePrompt}
        onImageSelect={openImageSelectionModal}
        onPromptEdit={openPromptModal}
        onRegenerateVoiceover={regenerateVoiceover}
        onRegenerateVideo={regenerateVideo}
        onPlayVoiceover={() => {}}
        onExpandVideo={handleExpandVideo}
        onDelete={deleteScene}
        onTemplateChange={updateSceneTemplate}
        onNegativePromptChange={updateSceneNegativePrompt}
      />

      <PropertySceneEditorModals
        imageSelectionModalOpen={imageSelectionModalOpen}
        closeImageSelectionModal={closeImageSelectionModal}
        imageSelectionScene={imageSelectionScene}
        processedImages={processedImages}
        aspectRatio={selectedAspectRatio}
        handleImageSelect={handleImageSelect}
        promptModalOpen={promptModalOpen}
        closePromptModal={closePromptModal}
        promptModalScene={promptModalScene}
        handlePromptSave={handlePromptSave}
        handlePromptSaveAndGenerate={handlePromptSaveAndGenerate}
        handleRegeneratePrompt={handleRegeneratePrompt}
        isRegeneratingPrompt={isRegeneratingPrompt}
        lightboxOpen={lightboxOpen}
        handleCloseLightbox={handleCloseLightbox}
        scenes={scenes}
        lightboxSceneIndex={lightboxSceneIndex}
      />
    </div>
  );
}

export const PropertySceneEditorMemo = memo(PropertySceneEditor);
